<template>
    <div v-if="value" class="scanner-overlay">
        <header class="scanner-header">
            <div class="scanner-header-back" @click="close">
                <v-icon color="primary" size="34">mdi-chevron-left</v-icon>
            </div>

            <p class="little-title mb-0">{{ title }}</p>
            <p v-if="instruction.length > 0" class="text">{{ instruction }}</p>
        </header>

        <div class="scanner-camera">
            <video ref="video" autoplay class="scanner-video" muted playsinline />

            <div v-if="feedback" :class="`scanner-alert--${feedback.status}`" class="scanner-alert">
                <v-icon color="white" size="22">{{ feedback.status === 'accepted' ? 'mdi-check-circle' : 'mdi-alert' }}</v-icon>

                <div>
                    <p class="scanner-alert-title mb-0">{{ $t(feedback.status === 'accepted' ? 'qr_code_added' : 'qr_code_not_added') }}</p>
                    <p class="scanner-alert-text mb-0">
                        {{ $t(feedback.status === 'accepted' ? 'number_scanned' : 'number_already_scanned', { number: feedback.code }) }}
                    </p>
                </div>
            </div>

            <div :class="feedback ? `scanner-frame--${feedback.status}` : ''" class="scanner-frame"><span /><span /><span /><span /></div>

            <div v-if="is_torch_supported" class="scanner-round-btn scanner-torch-btn" @click="toggleTorch">
                <v-icon color="primary">{{ is_torch_on ? 'mdi-flashlight-off' : 'mdi-flashlight' }}</v-icon>
            </div>

            <div v-if="has_several_cameras" class="scanner-round-btn scanner-switch-btn" @click="is_camera_picker_open = true">
                <v-icon color="primary">mdi-camera-flip</v-icon>
            </div>

            <div v-if="active_camera && error_code.length === 0" class="scanner-camera-name">
                <v-icon :color="active_camera.has_no_autofocus ? 'warning' : 'primary'" class="mr-2" size="16">
                    {{ active_camera.has_no_autofocus ? 'mdi-alert' : 'mdi-camera-outline' }}
                </v-icon>

                <span class="text-truncate">{{ active_camera.name }}</span>
            </div>

            <p v-if="is_starting" class="scanner-status">{{ $t('starting_camera') }}</p>

            <div v-if="error_code.length > 0" class="scanner-error">
                <v-icon class="mb-3" color="white" size="40">mdi-alert-circle-outline</v-icon>

                <p class="scanner-error-message mb-3">{{ $t(error_code) }}</p>

                <span v-if="error_detail.length > 0" class="scanner-error-code d-inline-block mb-5">{{ error_detail }}</span>

                <div class="scanner-error-actions">
                    <v-btn depressed @click="retry">{{ $t('retry') }}</v-btn>
                    <v-btn color="white" outlined @click="close">{{ $t('close') }}</v-btn>
                </div>
            </div>

            <!-- Held inside the overlay: a Vuetify menu mounts on the app root and would land under it -->
            <div v-if="is_camera_picker_open" class="scanner-picker" @click.self="is_camera_picker_open = false">
                <div class="scanner-picker-sheet">
                    <p class="scanner-picker-title mb-3">{{ $t('choose_camera') }}</p>

                    <div
                        v-for="camera in camera_options"
                        :key="camera.device_id"
                        class="scanner-picker-row"
                        @click="selectCamera(camera.device_id)"
                    >
                        <v-icon :color="camera.is_active ? 'primary' : 'grey lighten-1'" class="mr-3" size="22">
                            {{ camera.is_active ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                        </v-icon>

                        <div>
                            <p class="scanner-picker-name mb-0">{{ camera.name }}</p>
                            <p v-if="camera.label.length > 0" class="scanner-picker-label mb-0">{{ camera.label }}</p>
                            <p v-if="camera.has_no_autofocus" class="scanner-picker-warning mb-0">{{ $t('no_autofocus') }}</p>
                        </div>
                    </div>

                    <v-btn block class="mt-3" text @click="is_camera_picker_open = false">{{ $t('close') }}</v-btn>
                </div>
            </div>
        </div>

        <footer v-if="multiple" class="scanner-footer">
            <p class="text mt-0 mb-2">{{ $tc('codes_scanned_count', accepted_codes.length) }}</p>

            <div v-if="accepted_codes.length > 0" class="scanner-chips mb-4">
                <v-chip v-for="code in latest_codes_first" :key="code" class="scanner-chip" color="primary" outlined small>
                    {{ code }}
                </v-chip>
            </div>

            <!-- The overlay never closes by itself in multiple mode: leaving has to be an obvious action -->
            <v-btn block class="validation-button" large rounded @click="close">{{ $t('validate') }}</v-btn>
        </footer>
    </div>
</template>

<script>
import { getDecoder, READER_OPTIONS } from '@/plugins/zxingReader'

const SCAN_INTERVAL_MS = 80
const ACCEPTED_COOLDOWN_MS = 700
// Wide enough for a Code 128 label, tall enough for a QR code
const ROI_WIDTH_RATIO = 0.8
const ROI_HEIGHT_RATIO = 0.45

const SINGLE_SCAN_CLOSE_DELAY_MS = 400
const DUPLICATE_REPORT_INTERVAL_MS = 2000
const FEEDBACK_DURATION_MS = 1500
// Camera labels are both vendor- and locale-dependent: Android reports "camera2 0, facing back", iOS a translated
// "Back Camera". Anything unmatched keeps its enumeration rank, which already puts the main sensor first on Android.
const BACK_CAMERA_LABEL = /back|rear|arri[eè]re|trasera|traseira|posteriore|achter|hinten|r[uü]ck/i
const FRONT_CAMERA_LABEL = /front|user|avant|frontal|delantera|dianteira|anteriore|voor|vorder/i
const CAMERA_KIND_KEYS = { back: 'back_camera', front: 'front_camera', other: 'camera_lens' }

const ACCEPTED_VIBRATION_PATTERN = [60]
const DUPLICATE_VIBRATION_PATTERN = [40, 70, 40]

export default {
    name: 'ScannerComponent',
    props: {
        value: {
            type: Boolean,
            required: false,
            default: false,
        },
        multiple: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Seeds the session memory so codes already collected by the parent are refused straight away
        already_scanned_codes: {
            type: Array,
            required: false,
            default: () => [],
        },
        vibration: {
            type: Boolean,
            required: false,
            default: true,
        },
        title: {
            type: String,
            required: false,
            default: '',
        },
        instruction: {
            type: String,
            required: false,
            default: '',
        },
    },
    data() {
        return {
            is_starting: false,
            is_torch_supported: false,
            is_torch_on: false,
            cameras: [],
            active_camera_id: '',
            is_camera_picker_open: false,
            // Reactive: the picker marks the lenses this device has shown to be unable to focus
            no_autofocus_camera_ids: [],
            error_code: '',
            error_detail: '',
            accepted_codes: [],
            feedback: null,
        }
    },
    computed: {
        // The last code read has to stay in sight without scrolling the list
        latest_codes_first() {
            return this.accepted_codes.slice().reverse()
        },

        has_several_cameras() {
            return this.cameras.length > 1
        },

        // Vendor labels read like "camera2 0, facing back": usable to tell lenses apart, unreadable as a name
        camera_options() {
            const counters = { back: 0, front: 0, other: 0 }

            return this.cameras.map((camera) => {
                const kind = this.cameraKind(camera.label)
                counters[kind]++

                return {
                    device_id: camera.device_id,
                    name: this.$t(CAMERA_KIND_KEYS[kind], { index: counters[kind] }),
                    label: camera.label,
                    is_active: camera.device_id === this.active_camera_id,
                    has_no_autofocus: this.no_autofocus_camera_ids.indexOf(camera.device_id) !== -1,
                }
            })
        },

        active_camera() {
            return this.camera_options.find((camera) => camera.is_active) || null
        },

        // Absent from the persisted store of an installer who upgraded from an earlier build
        stored_camera_id() {
            return this.$store.state.scanner_camera_id || ''
        },
    },
    watch: {
        value(opened) {
            if (opened) {
                this.startSession()
                return
            }

            this.stopSession()
        },
        already_scanned_codes: {
            handler() {
                this.seedKnownCodes()
            },
            deep: true,
        },
    },
    created() {
        // Kept off `data` on purpose: Vue would proxy native media objects for no benefit and slow every frame down
        this.stream = null
        this.video_track = null
        this.scan_canvas = null
        this.scan_context = null
        this.is_scanning = false
        // Bumped on every open, close and camera switch: any async step resuming late must be able to tell it no
        // longer owns the camera
        this.session_id = 0
        // Bumped whenever the loop alone has to stop while the session keeps the camera, i.e. tab in the background
        this.loop_id = 0
        // Plain Set: read on every decoded frame, and nothing in the template depends on it
        this.known_codes = new Set()
        this.last_reported_code = ''
        this.last_reported_at = 0
        this.close_timeout_id = null
        this.feedback_timeout_id = null
        // Held until the overlay is down, so the parent is never called while it still covers the screen
        this.pending_code = ''
        // At most one automatic lens correction per open, otherwise a device that refuses the requested one loops
        this.has_realigned_camera = false
        // Bounds the automatic lens stepping, which restarts the camera on every move
        this.focus_realign_count = 0
        this.active_camera_focus_modes = null
        this.has_camera_capabilities = false
        // A lens the operator picked by hand is never corrected away from
        this.is_camera_choice_manual = false
    },
    mounted() {
        document.addEventListener('visibilitychange', this.handleVisibilityChange)

        if (this.value) {
            this.startSession()
        }
    },
    beforeDestroy() {
        this.session_id++
        this.retireScanLoop()
        this.stopCamera()
        this.clearFeedback()
        this.clearCloseTimeout()
        document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    },
    methods: {
        // region LIFECYCLE
        async startSession() {
            this.resetSession()
            this.clearCloseTimeout()
            this.has_realigned_camera = false
            this.focus_realign_count = 0
            this.no_autofocus_camera_ids = []
            this.is_camera_picker_open = false
            this.is_camera_choice_manual = false

            await this.startCameraAndScan()
        },

        // Also the entry point of the camera switch, which must keep the codes collected so far
        async startCameraAndScan() {
            const current_session = ++this.session_id

            this.retireScanLoop()
            this.stopCamera()
            this.error_code = ''
            this.error_detail = ''
            this.is_starting = true

            // The overlay is rendered by this watcher's own update, the video element does not exist yet
            await this.$nextTick()

            const is_decoder_ready = await this.loadDecoder()
            if (!is_decoder_ready || current_session !== this.session_id) {
                return
            }

            const is_camera_ready = await this.startCamera(current_session)

            // Clearing the flag on behalf of a session that has already been replaced would hide the indicator of
            // the startup still running
            if (current_session !== this.session_id) {
                return
            }

            this.is_starting = false

            if (!is_camera_ready) {
                return
            }

            await this.refreshCameras()
            if (current_session !== this.session_id) {
                return
            }

            if (this.realignOnPreferredCamera() || this.realignOnFocusCapableCamera()) {
                await this.startCameraAndScan()
                return
            }

            // Starting the loop on a backgrounded tab would only decode frozen frames; `visibilitychange` starts it
            // on the way back
            if (!document.hidden) {
                this.startScanLoop()
            }
        },

        stopSession() {
            this.session_id++
            this.retireScanLoop()
            this.is_starting = false
            this.stopCamera()
            this.clearFeedback()
            this.clearCloseTimeout()

            const scanned_code = this.pending_code
            this.pending_code = ''

            this.$emit('closed', this.accepted_codes.slice())

            // Reported once the overlay has left the DOM, as the replaced component did: the parent may move focus
            // or raise a snackbar, neither of which must happen behind a full-screen overlay
            if (scanned_code.length > 0) {
                this.$nextTick(() => this.$emit('scanned', scanned_code))
            }
        },

        handleVisibilityChange() {
            // Mobile browsers freeze the track in the background: the loop would decode the same frozen frame over and
            // over. Only the loop is retired here - retiring the session would strand a startup still awaiting the
            // decoder or the camera, leaving the overlay on its loading state with nothing left to restart it.
            if (document.hidden) {
                this.retireScanLoop()
                return
            }

            // A pending single-scan close means the session is already over; restarting would report a second code
            if (this.value && this.stream && !this.is_scanning && this.error_code.length === 0 && this.close_timeout_id === null) {
                this.startScanLoop()
            }
        },
        // endregion

        // region DECODER
        async loadDecoder() {
            try {
                await getDecoder()
                return true
            } catch (error) {
                // A short code rather than the raw message: enough to tell a blocking CSP from a filtering proxy
                // without putting a URL in front of the operator
                this.setError('decoder_load_failed', error.detail || '')
                return false
            }
        },
        // endregion

        // region CAMERA
        buildCameraConstraints() {
            const resolution = { width: { ideal: 1920 }, height: { ideal: 1080 } }

            if (this.stored_camera_id.length > 0) {
                return { video: Object.assign({ deviceId: { exact: this.stored_camera_id } }, resolution), audio: false }
            }

            return { video: Object.assign({ facingMode: { ideal: 'environment' } }, resolution), audio: false }
        },

        async startCamera(current_session) {
            if (!window.isSecureContext || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                this.setError('camera_unavailable_secure_context')
                return false
            }

            let opened_stream = null

            try {
                opened_stream = await navigator.mediaDevices.getUserMedia(this.buildCameraConstraints())
            } catch (error) {
                // The resolution is only an `ideal`, which never overconstrains: the remembered lens is the one this
                // device no longer offers. Forgetting it avoids paying for the failed attempt on every open
                if (error.name !== 'OverconstrainedError') {
                    this.setError(this.mapCameraErrorName(error.name))
                    return false
                }

                this.$store.commit('setScannerCameraId', '')

                try {
                    opened_stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
                } catch (fallback_error) {
                    this.setError(this.mapCameraErrorName(fallback_error.name))
                    return false
                }
            }

            // Closing while the permission prompt was up leaves this stream alive with nobody to stop it
            if (current_session !== this.session_id) {
                opened_stream.getTracks().forEach((track) => track.stop())
                return false
            }

            this.stream = opened_stream
            this.video_track = opened_stream.getVideoTracks()[0]
            this.active_camera_id = this.video_track.getSettings ? this.video_track.getSettings().deviceId || '' : ''

            const capabilities = this.video_track.getCapabilities ? this.video_track.getCapabilities() : null
            this.is_torch_supported = Boolean(capabilities && capabilities.torch)
            // Silence is not a fixed-focus lens: some browsers expose no capabilities at all, and reading that as
            // an absent autofocus would cycle through the lenses on every single open
            this.has_camera_capabilities = Boolean(capabilities && Object.keys(capabilities).length > 0)
            this.active_camera_focus_modes = (capabilities && capabilities.focusMode) || null

            this.rememberFocusCapability()

            await this.applyFocusMode(capabilities)

            if (current_session !== this.session_id || !this.$refs.video) {
                return false
            }

            this.$refs.video.srcObject = opened_stream

            try {
                await this.$refs.video.play()
            } catch (error) {
                // A closing session tears the element down, which rejects play() too: that is not an error to report
                if (current_session === this.session_id) {
                    this.setError('video_play_failed')
                }

                return false
            }

            return current_session === this.session_id
        },

        rememberFocusCapability() {
            const is_unusable =
                this.has_camera_capabilities &&
                this.active_camera_id.length > 0 &&
                !this.hasAutofocus(this.active_camera_focus_modes) &&
                this.no_autofocus_camera_ids.indexOf(this.active_camera_id) === -1

            if (is_unusable) {
                this.no_autofocus_camera_ids.push(this.active_camera_id)
            }
        },

        async applyFocusMode(capabilities) {
            const focus_modes = capabilities && capabilities.focusMode

            if (!this.video_track || !focus_modes || !focus_modes.includes('continuous')) {
                return
            }

            try {
                // Applied on its own: grouped with the torch, a single unsupported constraint would reject the batch
                await this.video_track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] })
            } catch (error) {
                // The camera keeps its default focus behaviour, which is still usable
            }
        },

        stopCamera() {
            if (this.$refs.video) {
                this.$refs.video.pause()
                this.$refs.video.srcObject = null
            }

            // Stopping the track also switches the torch off
            if (this.stream) {
                this.stream.getTracks().forEach((track) => track.stop())
            }

            this.stream = null
            this.video_track = null
            this.active_camera_id = ''
            this.is_torch_supported = false
            this.is_torch_on = false
        },

        mapCameraErrorName(error_name) {
            switch (error_name) {
                case 'NotAllowedError':
                case 'SecurityError':
                    return 'camera_permission_denied'
                case 'NotFoundError':
                case 'DevicesNotFoundError':
                    return 'no_camera_found'
                case 'NotReadableError':
                case 'TrackStartError':
                    return 'camera_busy'
                default:
                    return 'camera_error'
            }
        },
        // endregion

        // region TORCH AND CAMERA SWITCH
        async toggleTorch() {
            if (!this.video_track) {
                return
            }

            try {
                await this.video_track.applyConstraints({ advanced: [{ torch: !this.is_torch_on }] })
                this.is_torch_on = !this.is_torch_on
            } catch (error) {
                // The device advertised a torch it cannot actually drive
                this.is_torch_supported = false
            }
        },

        // Labels only carry content once the permission is granted, and the ordering below reads them
        async refreshCameras() {
            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                return
            }

            try {
                const video_inputs = (await navigator.mediaDevices.enumerateDevices())
                    .filter((device) => device.kind === 'videoinput')
                    .map((device) => ({ device_id: device.deviceId, label: device.label }))

                this.cameras = video_inputs
                    .filter((camera) => BACK_CAMERA_LABEL.test(camera.label))
                    .concat(video_inputs.filter((camera) => !BACK_CAMERA_LABEL.test(camera.label)))
            } catch (error) {
                // The switch button stays hidden, which is the safe default
            }
        },

        pickPreferredCameraId() {
            if (this.stored_camera_id.length > 0 && this.cameras.some((camera) => camera.device_id === this.stored_camera_id)) {
                return this.stored_camera_id
            }

            // Labels come back empty on some browsers, and without them a rear lens cannot be told from the selfie
            // camera. Leaving the browser's own pick alone is the only safe answer
            const back_cameras = this.cameras.filter((camera) => BACK_CAMERA_LABEL.test(camera.label))

            return back_cameras.length > 0 ? back_cameras[0].device_id : ''
        },

        // What the browser hands back for `environment` is often the ultra-wide lens of a multi-sensor phone, whose
        // minimum focus distance is too long for a dense label held at arm's length
        realignOnPreferredCamera() {
            const preferred_camera_id = this.pickPreferredCameraId()

            if (this.has_realigned_camera || preferred_camera_id.length === 0 || preferred_camera_id === this.active_camera_id) {
                return false
            }

            this.has_realigned_camera = true
            this.$store.commit('setScannerCameraId', preferred_camera_id)

            return true
        },

        // `single-shot` is enough: such a lens can still be driven to a sharp frame, unlike a fixed-focus one
        hasAutofocus(focus_modes) {
            return Boolean(focus_modes) && (focus_modes.includes('continuous') || focus_modes.includes('single-shot'))
        },

        // The ultra-wide and macro lenses of recent Galaxy phones are fixed-focus: they never sharpen a label held at
        // reading distance, and `environment` hands one back often enough to matter. A lens reporting no autofocus is
        // stepped over once; when none reports any, the remembered choice is dropped rather than pinning a lens
        // proven unable to focus
        realignOnFocusCapableCamera() {
            if (this.is_camera_choice_manual || !this.has_camera_capabilities || this.active_camera_id.length === 0) {
                return false
            }

            if (this.hasAutofocus(this.active_camera_focus_modes)) {
                this.$store.commit('setScannerCameraId', this.active_camera_id)

                return false
            }

            // A device that keeps handing back a lens other than the requested one would otherwise restart forever
            if (this.focus_realign_count >= this.cameras.length) {
                return false
            }

            // Only identified rear lenses are candidates: landing on the selfie camera is worse than a blurry frame
            const next_camera = this.cameras.find(
                (camera) => BACK_CAMERA_LABEL.test(camera.label) && this.no_autofocus_camera_ids.indexOf(camera.device_id) === -1,
            )

            if (!next_camera) {
                this.$store.commit('setScannerCameraId', '')

                return false
            }

            this.focus_realign_count++
            this.$store.commit('setScannerCameraId', next_camera.device_id)

            return true
        },

        cameraKind(label) {
            if (BACK_CAMERA_LABEL.test(label)) {
                return 'back'
            }

            return FRONT_CAMERA_LABEL.test(label) ? 'front' : 'other'
        },

        async selectCamera(device_id) {
            this.is_camera_picker_open = false

            if (device_id === this.active_camera_id) {
                return
            }

            // The automatic correction stands down for the rest of the session: an explicit pick outranks it
            this.is_camera_choice_manual = true
            this.$store.commit('setScannerCameraId', device_id)

            await this.startCameraAndScan()
        },
        // endregion

        // region SCAN LOOP
        startScanLoop() {
            this.is_scanning = true
            this.scanLoop(++this.loop_id)
        },

        // Retiring the loop rather than only clearing `is_scanning`: a loop parked in a sleep would otherwise resume
        // as a second one
        retireScanLoop() {
            this.is_scanning = false
            this.loop_id++
        },

        async scanLoop(current_loop) {
            const reader = await getDecoder()

            while (this.is_scanning && current_loop === this.loop_id) {
                let pause_ms = SCAN_INTERVAL_MS
                const image_data = this.grabRegionOfInterest()

                if (image_data) {
                    const decoded_text = await this.decodeFrame(reader, image_data)

                    if (decoded_text.length > 0 && this.is_scanning && current_loop === this.loop_id) {
                        pause_ms = this.handleDecodedText(decoded_text)
                    }
                }

                await this.sleep(pause_ms)
            }
        },

        async decodeFrame(reader, image_data) {
            try {
                const results = await reader.readBarcodes(image_data, READER_OPTIONS)
                const decoded = results.find((result) => result.isValid && result.text.length > 0)

                return decoded ? decoded.text : ''
            } catch (error) {
                // A frame the decoder chokes on must not end the session
                return ''
            }
        },

        grabRegionOfInterest() {
            if (!this.$refs.video) {
                return null
            }

            const width = this.$refs.video.videoWidth
            const height = this.$refs.video.videoHeight

            // The first frames after play() can still report a 0x0 video size
            if (!width || !height) {
                return null
            }

            if (!this.scan_context || !this.scan_canvas) {
                this.scan_canvas = document.createElement('canvas')
                // Without this hint Chrome keeps the canvas GPU backed and every getImageData round-trips
                this.scan_context = this.scan_canvas.getContext('2d', { willReadFrequently: true })

                if (!this.scan_context) {
                    return null
                }
            }

            const region_width = Math.round(width * ROI_WIDTH_RATIO)
            const region_height = Math.round(height * ROI_HEIGHT_RATIO)

            if (this.scan_canvas.width !== region_width || this.scan_canvas.height !== region_height) {
                this.scan_canvas.width = region_width
                this.scan_canvas.height = region_height
            }

            // Source and destination sizes are identical: no downscale, dense bars keep their native pixel density
            this.scan_context.drawImage(
                this.$refs.video,
                Math.round((width - region_width) / 2),
                Math.round((height - region_height) / 2),
                region_width,
                region_height,
                0,
                0,
                region_width,
                region_height,
            )

            return this.scan_context.getImageData(0, 0, region_width, region_height)
        },

        sleep(duration_ms) {
            return new Promise((resolve) => setTimeout(resolve, duration_ms))
        },
        // endregion

        // region SESSION
        // Rebuilt rather than added to: a code the parent drops from its own list has to become scannable again
        seedKnownCodes() {
            this.known_codes = new Set(this.already_scanned_codes.map((code) => code.trim()).concat(this.accepted_codes))
        },

        resetSession() {
            this.accepted_codes = []
            this.last_reported_code = ''
            this.last_reported_at = 0
            this.pending_code = ''
            this.seedKnownCodes()
        },

        handleDecodedText(text) {
            const code = text.trim()

            if (code.length === 0) {
                return SCAN_INTERVAL_MS
            }

            return this.known_codes.has(code) ? this.reportDuplicate(code) : this.acceptCode(code)
        },

        reportDuplicate(code) {
            // The label stays in front of the lens and would otherwise be reported a dozen times a second
            if (code === this.last_reported_code && Date.now() - this.last_reported_at < DUPLICATE_REPORT_INTERVAL_MS) {
                return SCAN_INTERVAL_MS
            }

            this.rememberReport(code)
            this.showFeedback('duplicate', code)
            this.$emit('duplicate', code)

            return SCAN_INTERVAL_MS
        },

        acceptCode(code) {
            this.known_codes.add(code)
            this.accepted_codes.push(code)
            this.rememberReport(code)
            this.showFeedback('accepted', code)

            if (this.multiple) {
                this.$emit('scanned', code)

                return ACCEPTED_COOLDOWN_MS
            }

            // Short delay so the operator still sees what was read before the overlay disappears. Closing by hand
            // during it still reports the code, since `stopSession` is what hands it over
            this.is_scanning = false
            this.pending_code = code
            this.close_timeout_id = setTimeout(this.close, SINGLE_SCAN_CLOSE_DELAY_MS)

            return SCAN_INTERVAL_MS
        },

        rememberReport(code) {
            this.last_reported_code = code
            this.last_reported_at = Date.now()
        },

        clearCloseTimeout() {
            if (this.close_timeout_id) {
                clearTimeout(this.close_timeout_id)
                this.close_timeout_id = null
            }
        },
        // endregion

        // region FEEDBACK
        showFeedback(status, code) {
            this.feedback = { status: status, code: code }
            this.triggerVibration(status === 'accepted' ? ACCEPTED_VIBRATION_PATTERN : DUPLICATE_VIBRATION_PATTERN)

            if (this.feedback_timeout_id) {
                clearTimeout(this.feedback_timeout_id)
            }

            this.feedback_timeout_id = setTimeout(() => {
                this.feedback = null
                this.feedback_timeout_id = null
            }, FEEDBACK_DURATION_MS)
        },

        clearFeedback() {
            if (this.feedback_timeout_id) {
                clearTimeout(this.feedback_timeout_id)
                this.feedback_timeout_id = null
            }

            this.feedback = null
        },

        triggerVibration(pattern) {
            // Unsupported on iOS, where the visual feedback is the only one left
            if (!this.vibration || typeof navigator.vibrate !== 'function') {
                return
            }

            navigator.vibrate(pattern)
        },
        // endregion

        // region ERRORS
        setError(code, detail = '') {
            this.error_code = code
            this.error_detail = detail
            this.is_starting = false
            this.$emit('error', { code: code, message: this.$t(code), detail: detail })
        },

        retry() {
            this.startCameraAndScan()
        },
        // endregion

        close() {
            this.$emit('input', false)
        },
    },
}
</script>

<style scoped>
.scanner-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 3000;
    display: flex;
    flex-direction: column;
    background-color: #000000;
}

.scanner-header {
    position: relative;
    flex: 0 0 auto;
    /* Insets: in standalone PWA mode the overlay runs under the notch and the home indicator */
    padding: calc(16px + env(safe-area-inset-top)) 56px 16px;
    text-align: center;
    background-color: #f5f7fb;
}

/* A plain wrapper rather than the class on the v-icon: Vuetify doubles its own selector and wins on equal weight */
.scanner-header-back {
    position: absolute;
    top: 50%;
    left: 8px;
    display: flex;
    padding: 4px;
    transform: translateY(-50%);
    cursor: pointer;
}

.scanner-camera {
    position: relative;
    flex: 1 1 auto;
    overflow: hidden;
}

/* The global `video` rule in app.scss sets a relative position that would break the overlay layout */
.scanner-video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.scanner-alert {
    position: absolute;
    top: 16px;
    left: 16px;
    right: 16px;
    z-index: 1;
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 12px;
    color: #ffffff;
}

.scanner-alert--accepted {
    background-color: rgba(76, 175, 80, 0.85);
}

.scanner-alert--duplicate {
    background-color: rgba(251, 140, 0, 0.85);
}

.scanner-alert-title {
    font-size: 16px;
    font-weight: 600;
}

.scanner-alert-text {
    font-size: 16px;
}

/* The decoder crops the centre of the native frame with these same ratios */
.scanner-frame {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 80%;
    height: 45%;
    transform: translate(-50%, -50%);
    color: rgba(255, 255, 255, 0.9);
    pointer-events: none;
    transition: color 120ms ease-out;
}

.scanner-frame--accepted {
    color: #4caf50;
}

.scanner-frame--duplicate {
    color: #fb8c00;
}

.scanner-frame span {
    position: absolute;
    width: 56px;
    height: 56px;
    border: 4px solid currentColor;
}

.scanner-frame span:nth-child(1) {
    top: 0;
    left: 0;
    border-right: none;
    border-bottom: none;
    border-top-left-radius: 10px;
}

.scanner-frame span:nth-child(2) {
    top: 0;
    right: 0;
    border-left: none;
    border-bottom: none;
    border-top-right-radius: 10px;
}

.scanner-frame span:nth-child(3) {
    bottom: 0;
    left: 0;
    border-right: none;
    border-top: none;
    border-bottom-left-radius: 10px;
}

.scanner-frame span:nth-child(4) {
    bottom: 0;
    right: 0;
    border-left: none;
    border-top: none;
    border-bottom-right-radius: 10px;
}

.scanner-round-btn {
    position: absolute;
    bottom: 16px;
    z-index: 1;
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    background-color: #f5f7fb;
    cursor: pointer;
}

.scanner-torch-btn {
    left: 16px;
}

.scanner-switch-btn {
    right: 16px;
}

.scanner-camera-name {
    position: absolute;
    bottom: 26px;
    left: 50%;
    z-index: 1;
    max-width: calc(100% - 140px);
    display: flex;
    align-items: center;
    padding: 4px 12px;
    transform: translateX(-50%);
    border-radius: 100px;
    background-color: rgba(0, 0, 0, 0.55);
    color: #ffffff;
    font-size: 12px;
}

.scanner-picker {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-end;
    background-color: rgba(0, 0, 0, 0.6);
}

.scanner-picker-sheet {
    width: 100%;
    max-height: 100%;
    overflow-y: auto;
    padding: 16px;
    border-radius: 16px 16px 0 0;
    background-color: #ffffff;
}

.scanner-picker-title {
    color: #4c545f;
    font-size: 16px;
    font-weight: bold;
}

.scanner-picker-row {
    display: flex;
    align-items: center;
    padding: 10px 4px;
    cursor: pointer;
}

.scanner-picker-row + .scanner-picker-row {
    border-top: 1px solid #eceff5;
}

.scanner-picker-name {
    color: #4c545f;
    font-size: 14px;
}

.scanner-picker-label {
    color: #9aa2ae;
    font-size: 11px;
    word-break: break-all;
}

.scanner-picker-warning {
    color: #f4a100;
    font-size: 11px;
}

.scanner-status {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #ffffff;
    font-size: 14px;
}

.scanner-error {
    position: absolute;
    top: 50%;
    left: 16px;
    right: 16px;
    z-index: 1;
    transform: translateY(-50%);
    padding: 32px 24px 28px;
    border-radius: 16px;
    background-color: rgba(0, 0, 0, 0.88);
    color: #ffffff;
    text-align: center;
}

.scanner-error-message {
    font-size: 16px;
    line-height: 1.45;
}

/* Diagnostic code, read out to support rather than acted on: present but visibly secondary */
.scanner-error-code {
    padding: 3px 12px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 11px;
    letter-spacing: 0.08em;
}

.scanner-error-actions {
    display: flex;
    justify-content: center;
    gap: 8px;
}

.scanner-footer {
    flex: 0 0 auto;
    padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
    text-align: center;
    background-color: #f5f7fb;
}

.scanner-chips {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    /* `safe` keeps the first chips reachable once the list overflows, where plain `center` would clip them */
    justify-content: safe center;
    scrollbar-width: none;
}

.scanner-chips::-webkit-scrollbar {
    display: none;
}

.scanner-chip {
    flex: 0 0 auto;
}
</style>
